import React, { useEffect, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import { ChevronDown, Mic, Pencil, RefreshCw, X } from 'lucide-react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import SoftDepthScope from '../../../../design-system/SoftDepthScope';
import LegacyThemeScope from '../../../../design-system/LegacyThemeScope';
import {
  POLICY_CATEGORIES,
  UNSET_PERSONALITY,
  UNSPECIFIED_GENDER,
  createDefaultClientFilters,
  editFilterArray
} from '../clientFilters';
import type {
  BirthdayFilter,
  ClientFilterOptions,
  ClientFilters,
  LastMeetingFilter,
  PolicyCategory
} from '../clientFilters';
import type { IntlLike } from '../clientPresentation';
import { DESKTOP_MEDIA, SHEET_MEDIA } from '../clientLayout';

// @soft-depth — the whole panel, its chips, buttons and form controls, takes its
// look from the Soft Depth theme; only the aside keeps a class, for placement.

type MultiFilterKey = 'importance' | 'potential' | 'gender' | 'existingClient' | 'source' | 'personality';

/**
 * Chip labels for the two policy-category groups. Every id already exists in
 * all three locale files — the gap labels are the same ones
 * `LEGACY_STATE_LABEL_KEYS` uses for the dashboard's coverage-gap lists, so the
 * two entry points into 「缺乏醫療」 read identically even though they are
 * computed differently (see the findings doc §4.1).
 */
const POLICY_HOLDING_LABEL_KEYS: Record<PolicyCategory, string> = {
  life: 'Life',
  ci: 'Critical Illness',
  medical: 'Medical',
  saving: 'Saving',
  ilas: 'ILAS'
};

const POLICY_GAP_LABEL_KEYS: Record<PolicyCategory, string> = {
  life: 'Without Life',
  ci: 'Without C.I.',
  medical: 'Without Medical',
  saving: 'Without Saving',
  ilas: 'Without ILAS'
};

export type FilterVoiceButtonProps = {
  label: string;
};

// 語音篩選: a full, always-labelled button on every screen size, unlike the
// icon row above it (clientv2-command-button's mobile rule squashes labels
// away — this lives outside that class on purpose). It fills in the very
// filters below it, so it opens the drawer's own top rather than a slot in
// the command bar.
//
// It is rendered inside GeneralVoiceWithAiButton, which the sidebar keeps on
// the legacy theme (its RecordDialog was built for it), so the button re-enters
// Soft Depth itself; the bare scope inherits business / recruit from outside.
// `flex: 1 1 0` lets 重置 beside it keep its width: the wrappers in between are
// `display: contents`, so this button is the row's flex item.
export const FilterVoiceButton = ({ label }: FilterVoiceButtonProps) => (
  <SoftDepthScope>
    <Button
      variant="contained"
      startIcon={<Mic size={16} aria-hidden="true" />}
      aria-label={label}
      title={label}
      sx={{ flex: '1 1 0', minWidth: 0, py: 1.25, typography: 'body1', fontWeight: 700 }}
    >
      {label}
    </Button>
  </SoftDepthScope>
);

type FilterLabelProps = {
  children: React.ReactNode;
  htmlFor?: string;
};

const FilterLabel = ({ children, htmlFor }: FilterLabelProps) => (
  <Typography
    variant="subtitle2"
    color="text.secondary"
    {...(htmlFor ? { component: 'label', htmlFor } : { component: 'div' })}
    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5, mb: 0.75 }}
  >
    {children}
  </Typography>
);

type FilterChipProps = {
  label: React.ReactNode;
  active: boolean;
  onClick: () => void;
  /** The family's tint; unselected chips of the default family are plain grey. */
  color?: 'primary' | 'warning';
  icon?: React.ReactElement;
  /** 'small' for long runs of names (families, tags), where the rows add up. */
  size?: 'small' | 'medium';
  /**
   * A tag's own colour (adviser data, not a design value, so it goes on as an
   * inline style). A tag chip stays outlined in that colour: off it is dimmed,
   * on it is at full strength.
   */
  tagColor?: string | null;
};

/** One filter option: outlined while off, the soft filled pill while on. */
const FilterChip = ({ label, active, onClick, color = 'primary', icon, size, tagColor }: FilterChipProps) => {
  const isTag = tagColor !== undefined;
  return (
    <Chip
      component="button"
      clickable
      size={size}
      label={label}
      icon={icon}
      variant={active && !isTag ? 'filled' : 'outlined'}
      color={(active && !isTag) || color !== 'primary' ? color : 'default'}
      aria-pressed={active}
      onClick={onClick}
      style={tagColor ? { color: tagColor, borderColor: tagColor } : undefined}
      sx={isTag ? { opacity: active ? 1 : 0.45, fontWeight: active ? 700 : 600 } : undefined}
    />
  );
};

const ChipRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Stack direction="row" role="group" aria-label={label} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
    {children}
  </Stack>
);

// Enough to be worth a glance without turning the panel into a scrolling list
// of names; anything longer is faster to narrow by typing.
const REFERRER_SUGGESTION_LIMIT = 6;

type ReferrerFieldProps = {
  value: string;
  /** Referrer names already used on the adviser's factsheets. */
  options: string[];
  label: string;
  placeholder: string;
  onChange: (value: string) => void;
};

/**
 * 介紹人, as V1 has it: a freeSolo Autocomplete over the names already on the
 * adviser's factsheets (searchFilter.js:670-680), not a bare text box. Typing a
 * name nobody has been referred by is still allowed — that is what freeSolo
 * means and what the filter itself supports — the list only saves the adviser
 * from having to remember the exact spelling of one they have used before.
 *
 * Still hand-rolled rather than MUI's Autocomplete, now that the panel is on
 * MUI: the behaviour below (substring match like the filter itself, a 6-name
 * cap, nothing offered once the field holds the only match, close on an outside
 * press) is what advisers already use, and Autocomplete closes on blur rather
 * than on an outside press. Only the look moved to the theme: OutlinedInput for
 * the field, a pop-shadow Paper for the list.
 */
const ReferrerField = ({ value, options, label, placeholder, onChange }: ReferrerFieldProps) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocument = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocument);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onDocument);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  const term = value.trim().toLowerCase();
  // Same substring, same case-insensitivity as the filter this feeds
  // (matchesFactsheetFilters), so a suggestion can never fail to match itself.
  const matches = options.filter(option => !term || option.toLowerCase().includes(term));
  // Nothing left to choose once the field already holds the only match.
  const exhausted = matches.length === 1 && matches[0].toLowerCase() === term;
  const suggestions = exhausted ? [] : matches.slice(0, REFERRER_SUGGESTION_LIMIT);
  const listOpen = open && suggestions.length > 0;

  return (
    // The list floats over whatever follows rather than pushing the rest of the
    // accordion down as the adviser types.
    <Box ref={wrapRef} sx={{ position: 'relative' }}>
      <OutlinedInput
        id="clientv2-filter-referrer"
        fullWidth
        value={value}
        placeholder={placeholder}
        inputProps={{
          role: 'combobox',
          'aria-expanded': listOpen,
          'aria-controls': 'clientv2-filter-referrer-list',
          'aria-autocomplete': 'list',
          // The suggestions ARE the autocomplete; the browser's own dropdown on
          // top of them would cover the list with the same names.
          autoComplete: 'off'
        }}
        onFocus={() => setOpen(true)}
        onChange={event => {
          onChange(event.target.value);
          setOpen(true);
        }}
        sx={{ '& input': { py: 0.875 } }}
      />
      {listOpen && (
        <Paper
          component="ul"
          elevation={8}
          id="clientv2-filter-referrer-list"
          role="listbox"
          aria-label={label}
          sx={{
            position: 'absolute',
            top: '100%',
            right: 0,
            left: 0,
            zIndex: 5,
            maxHeight: 168,
            m: 0,
            mt: 0.375,
            p: 0.5,
            overflowY: 'auto',
            listStyle: 'none',
            borderRadius: 1.5
          }}
        >
          {suggestions.map(name => (
            <li key={name}>
              <ButtonBase
                role="option"
                data-testid="referrer-suggestion"
                aria-selected={name === value}
                // Focus has to stay in the field: losing it would fire the
                // outside-click close before the click ever lands.
                onMouseDown={event => event.preventDefault()}
                onClick={() => {
                  onChange(name);
                  setOpen(false);
                }}
                sx={{
                  width: '100%',
                  justifyContent: 'flex-start',
                  px: 1.125,
                  py: 0.875,
                  borderRadius: 1,
                  typography: 'body2',
                  color: 'text.primary',
                  textAlign: 'left',
                  '&:hover': { bgcolor: 'grey.100' },
                  '&.Mui-focusVisible': { outline: 2, outlineColor: 'primary.main', outlineOffset: -2 }
                }}
              >
                {name}
              </ButtonBase>
            </li>
          ))}
        </Paper>
      )}
    </Box>
  );
};

export type SmartFilterSidebarProps = {
  filters: ClientFilters;
  options: ClientFilterOptions;
  onChange: (filters: ClientFilters) => void;
  /**
   * Explicit "clear everything" path, mirroring V1's `resetSearch()` — which
   * clears `clientState`, `searchTerm` and `filterObject` together. A plain
   * filter change (V1 `applySearchFilter`) must NOT do that, so reset cannot
   * just be another `onChange`. Falls back to `onChange(defaults)` when the
   * host does not supply it, keeping this leaf independently testable.
   */
  onReset?: () => void;
  intl: IntlLike;
  currency?: string;
  mobileOpen?: boolean;
  onClose?: () => void;
  onManageFamilies?: () => void;
  voiceControl?: React.ReactNode;
  /**
   * The calendar view, where filters would not change what is on screen. The
   * panel stays where it is (it is a standing column on desktop) but greyed and
   * `inert`, so neither a pointer nor the keyboard can change a filter the
   * adviser cannot see take effect.
   */
  disabled?: boolean;
};

type ChipOption = {
  value: string | number;
  label: string;
  /** A theme palette path for a signal dot before the label, e.g. 'cpi.high'. */
  dot?: string;
};

type ChipGroupProps = {
  label: string;
  options: ChipOption[];
  values: Array<string | number>;
  allLabel: string;
  onAll: () => void;
  onToggle: (value: string | number) => void;
};

const ChipGroup = ({ label, options, values, allLabel, onAll, onToggle }: ChipGroupProps) => (
  <>
    <FilterLabel>{label}</FilterLabel>
    <ChipRow label={label}>
      <FilterChip label={allLabel} active={values.length === 0} onClick={onAll} />
      {options.map(option => (
        <FilterChip
          key={String(option.value)}
          label={option.label}
          active={values.includes(option.value)}
          onClick={() => onToggle(option.value)}
          icon={
            option.dot ? (
              <Box
                component="span"
                aria-hidden="true"
                sx={{ width: 7, height: 7, flex: '0 0 auto', borderRadius: '50%', bgcolor: option.dot }}
              />
            ) : undefined
          }
        />
      ))}
    </ChipRow>
  </>
);

type RangeControlProps = {
  label: string;
  value: number;
  max: number;
  step: number;
  /**
   * The comparison the filter applies, shown as the direct-entry box's prefix
   * exactly as V1 does (searchFilter.js NumberFormat): coverage and saving are
   * "at or below" (<), income is "at least" (>). See clientFilters.ts.
   */
  comparator: '<' | '>';
  onChange: (value: number) => void;
};

/**
 * V1's coverage control (searchFilter.js:883-1077): a slider and a direct-entry
 * box for one threshold, each on a line of its own under the label. In a 224px
 * column there is no room to pair anything: beside the box, labels such as
 * 每月儲蓄 (HKD/月) broke over two lines, and beside the slider, the box left the
 * slider too short to drag with any precision. Typing is NOT clamped to the
 * slider's max — V1's NumberFormat isn't either, and the filter itself works
 * with any threshold; the slider thumb just parks at its end. Digits only,
 * empty means 0 (filter off), thousand separators on the readback like V1's
 * NumberFormat.
 */
const RangeControl = ({ label, value, max, step, comparator, onChange }: RangeControlProps) => (
  <Box role="group" aria-label={label} sx={{ mt: 0.75 }}>
    <Typography variant="caption" component="div" color="text.secondary" sx={{ fontWeight: 600 }}>
      {label}
    </Typography>
    <OutlinedInput
      fullWidth
      value={value > 0 ? value.toLocaleString() : ''}
      placeholder="0"
      startAdornment={
        <InputAdornment position="start" aria-hidden="true">
          {comparator}
        </InputAdornment>
      }
      inputProps={{ inputMode: 'numeric', autoComplete: 'off', 'aria-label': label }}
      onChange={event => {
        const digits = event.target.value.replace(/[^0-9]/g, '');
        onChange(digits ? Number(digits) : 0);
      }}
      sx={{
        mt: 0.5,
        '& input': {
          py: 0.375,
          pr: 1,
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
          // A threshold that is switched on reads in the accent, so a glance
          // down the four rows shows which ones are filtering.
          ...(value > 0 && { color: 'primary.dark', fontWeight: 700 })
        }
      }}
    />
    <Slider
      min={0}
      max={max}
      step={step}
      // A typed value past the slider max still filters (V1 behaviour); the
      // thumb just parks at the end, and the next drag snaps it back.
      value={Math.min(value, max)}
      aria-label={label}
      onChange={(_event, next) => onChange(next as number)}
    />
  </Box>
);

const SmartFilterSidebar = ({
  filters,
  options,
  onChange,
  onReset,
  intl,
  currency = 'HKD',
  mobileOpen = false,
  onClose,
  onManageFamilies,
  voiceControl,
  disabled = false
}: SmartFilterSidebarProps) => {
  const [specialOpen, setSpecialOpen] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const sidebarRef = useRef<HTMLElement | null>(null);

  // `inert` is set by hand: React 17 has no typed prop for it.
  useEffect(() => {
    const node = sidebarRef.current;
    if (!node) return;
    if (disabled) node.setAttribute('inert', '');
    else node.removeAttribute('inert');
  }, [disabled]);
  const message = (id: string) => intl.formatMessage({ id });
  const allLabel = message('clientv2-filter-all');

  useEffect(() => {
    if (mobileOpen) closeButtonRef.current?.focus();
  }, [mobileOpen]);

  const toggleMulti = (key: MultiFilterKey, value: string | number) => {
    const current = filters[key] as Array<string | number>;
    onChange({ ...filters, [key]: editFilterArray(current, value) });
  };

  const togglePolicy = (key: 'policyHoldings' | 'policyGaps', value: string | number) =>
    onChange({ ...filters, [key]: editFilterArray(filters[key], value as PolicyCategory) });

  const setBirthday = (birthday: BirthdayFilter) => {
    onChange({
      ...filters,
      birthday: filters.birthday === birthday ? 'all' : birthday,
      birthdayMonth: birthday === 'month' ? filters.birthdayMonth : 0
    });
  };

  const setLastMeeting = (value: LastMeetingFilter) => onChange({ ...filters, lastMeetingMonths: value });

  const birthdayOptions: Array<{ value: BirthdayFilter; label: string }> = [
    { value: 'this-month', label: message('This-mth') },
    { value: 'next-1', label: message('clientv2-filter-birthday-next-1') },
    { value: 'next-2', label: message('clientv2-filter-birthday-next-2') },
    { value: 'next-3', label: message('clientv2-filter-birthday-next-3') }
  ];

  const months = [
    'Jan-mth',
    'Feb-mth',
    'Mar-mth',
    'Apr-mth',
    'May-mth',
    'Jun-mth',
    'Jul-mth',
    'Aug-mth',
    'Sep-mth',
    'Oct-mth',
    'Nov-mth',
    'Dec-mth'
  ];

  const personalityOptions = ['D', 'I', 'S', 'C', UNSET_PERSONALITY];

  return (
    // The aside keeps its class for placement only: the desktop column, the
    // phone's slide-out sheet and the entrance animation. The panel inside
    // takes its whole look from the Soft Depth theme (component.tsx wraps the
    // sidebar in <SoftDepthScope>).
    <aside
      id="clientv2-smart-filter"
      ref={sidebarRef}
      className={`clientv2-filter-sidebar${mobileOpen ? ' is-open' : ''}`}
      aria-label={message('clientv2-filter-title')}
      aria-disabled={disabled || undefined}
      title={disabled ? message('clientv2-calendar-filter-off') : undefined}
      data-testid="smart-filter"
    >
      <Paper
        sx={{
          p: 2,
          // The calendar view switches the panel off; `inert` stops the input,
          // this is the visual half.
          ...(disabled && { opacity: 0.5, cursor: 'not-allowed' }),
          // On a phone the panel is the sheet's content, so the card flattens
          // into the sheet rather than framing a second surface inside it.
          [SHEET_MEDIA]: { p: 0, boxShadow: 'none', borderRadius: 0, bgcolor: 'transparent' }
        }}
      >
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="h6" component="h2">
            {message('clientv2-filter-title')}
          </Typography>
          <IconButton
            ref={closeButtonRef}
            aria-label={message('clientv2-filter-close')}
            onClick={onClose}
            sx={{
              display: 'inline-flex',
              width: 28,
              height: 28,
              borderRadius: 1,
              bgcolor: 'grey.300',
              color: 'grey.600',
              '&:hover': { bgcolor: 'grey.300' },
              [DESKTOP_MEDIA]: { display: 'none' }
            }}
          >
            <X size={16} aria-hidden="true" />
          </IconButton>
        </Stack>

        {/* 重置 sits on this row rather than up in the heading, and 語音揀客
            gives up the width for it. Two reasons it is better here: in the
            heading it was 12px grey text tucked between a title and a ✕, which
            is where a control goes to be missed; and it belongs beside the
            other thing that changes every filter at once, not beside the panel's
            name.

            The row renders even without `voiceControl` — that prop is optional
            (the sidebar's own suite renders without it), and 重置 must not
            disappear along with a button it has nothing to do with.

            `alignItems: stretch` keeps 重置 exactly as tall as the blue button
            without either one being given a height to hold in step. */}
        <Stack direction="row" sx={{ alignItems: 'stretch', gap: 1, mb: 1.75 }}>
          <LegacyThemeScope>{voiceControl}</LegacyThemeScope>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<RefreshCw size={16} aria-hidden="true" />}
            onClick={() => (onReset ? onReset() : onChange(createDefaultClientFilters()))}
            sx={{ flex: '0 0 auto', px: 1.5 }}
          >
            {message('clientv2-filter-reset')}
          </Button>
        </Stack>

        <ChipGroup
          label={message('clientv2-filter-tier')}
          allLabel={allLabel}
          values={filters.importance}
          options={[
            { value: 'V.VIP', label: 'VVIP' },
            { value: 'VIP', label: 'VIP' },
            { value: 'Normal', label: message('Normal') }
          ]}
          onAll={() => onChange({ ...filters, importance: [] })}
          onToggle={value => toggleMulti('importance', value)}
        />

        {/* 現有客戶 sits with 客戶等級 rather than behind 特定客群篩選, where it
            used to live among gender, source and DISC. It is not a special cut
            of the book — it is the first thing an adviser sorts the book BY
            (who already bought, and who has not), and the drawer pairs it with
            重要性 in one 客戶狀態 popup for that reason. A filter kept one tap
            further away than the fact it filters on is the wrong way round. */}
        <ChipGroup
          label={message('existing-client')}
          allLabel={allLabel}
          values={filters.existingClient}
          options={[
            { value: 'Yes', label: message('Yes') },
            { value: 'No', label: message('No') }
          ]}
          onAll={() => onChange({ ...filters, existingClient: [] })}
          onToggle={value => toggleMulti('existingClient', value)}
        />

        <ChipGroup
          label={message('clientv2-cpi-label')}
          allLabel={allLabel}
          values={filters.potential}
          options={[
            { value: 0, label: message('clientv2-cpi-high'), dot: 'cpi.high' },
            { value: 1, label: message('clientv2-cpi-good'), dot: 'cpi.good' },
            { value: 2, label: message('clientv2-cpi-fair'), dot: 'cpi.fair' }
          ]}
          onAll={() => onChange({ ...filters, potential: [] })}
          onToggle={value => toggleMulti('potential', value)}
        />

        <FilterLabel>{message('clientv2-birthday')}</FilterLabel>
        <ChipRow label={message('clientv2-birthday')}>
          {birthdayOptions.map(option => (
            <FilterChip
              key={option.value}
              label={option.label}
              active={filters.birthday === option.value}
              onClick={() => setBirthday(option.value)}
            />
          ))}
          {/* `native`: the phone's own month picker beats a menu on a sheet
              that is already a scrolling overlay. It takes the rest of the
              chips' wrapping row. */}
          <Select
            native
            value={filters.birthday === 'month' ? filters.birthdayMonth : 0}
            inputProps={{ 'aria-label': message('clientv2-filter-birthday-month') }}
            onChange={event => {
              const birthdayMonth = Number(event.target.value);
              onChange({ ...filters, birthday: birthdayMonth ? 'month' : 'all', birthdayMonth });
            }}
            sx={{
              flex: '1 1 130px',
              minWidth: 130,
              height: 30,
              ...(filters.birthday === 'month' && { color: 'primary.dark', fontWeight: 700 })
            }}
          >
            <option value={0}>{message('clientv2-filter-birthday-month')}</option>
            {months.map((month, index) => (
              <option key={month} value={index + 1}>
                {message(month)}
              </option>
            ))}
          </Select>
        </ChipRow>

        <FilterLabel>{message('clientv2-filter-last-meeting')}</FilterLabel>
        <ChipRow label={message('clientv2-filter-last-meeting')}>
          {[
            { value: 0 as LastMeetingFilter, label: allLabel },
            { value: 1 as LastMeetingFilter, label: message('clientv2-filter-over-1-month') },
            { value: 3 as LastMeetingFilter, label: message('clientv2-filter-over-3-months') },
            { value: 6 as LastMeetingFilter, label: message('clientv2-filter-over-6-months') },
            { value: 12 as LastMeetingFilter, label: message('clientv2-filter-over-year') }
          ].map(option => (
            <FilterChip
              key={option.value}
              label={option.label}
              active={filters.lastMeetingMonths === option.value}
              onClick={() => setLastMeeting(option.value)}
            />
          ))}
        </ChipRow>

        <Divider sx={{ my: 1.75, borderColor: 'grey.200' }} />

        <Box component="section" sx={{ overflow: 'hidden', border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
          <ButtonBase
            aria-expanded={specialOpen}
            aria-controls="clientv2-special-filters"
            onClick={() => setSpecialOpen(open => !open)}
            sx={{
              width: '100%',
              justifyContent: 'flex-start',
              gap: 1,
              px: 1.5,
              py: 1.375,
              typography: 'body1',
              fontWeight: 700,
              textAlign: 'left',
              color: specialOpen ? 'primary.dark' : 'text.primary',
              bgcolor: specialOpen ? 'primary.soft' : 'grey.50',
              '&.Mui-focusVisible': { outline: 2, outlineColor: 'primary.main', outlineOffset: -2 }
            }}
          >
            {/* 「更多篩選」 and nothing else. The heading used to be 特定客群篩選
                followed by a 「性別 · 年齡 · 來源 · …」 list of everything
                inside — seven terms in 11px grey that were ellipsised away on a
                phone anyway, spent on describing a section the chevron already
                offers to open. */}
            <Box component="span" sx={{ flex: '1 1 auto', minWidth: 0 }}>
              {message('clientv2-filter-specific')}
            </Box>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                color: specialOpen ? 'primary.dark' : 'text.disabled',
                transition: 'transform 200ms ease',
                transform: specialOpen ? 'rotate(180deg)' : 'none'
              }}
            >
              <ChevronDown size={16} aria-hidden="true" />
            </Box>
          </ButtonBase>

          <Box id="clientv2-special-filters" hidden={!specialOpen} sx={{ px: 1.5, pt: 0.25, pb: 1.625 }}>
            <ChipGroup
              label={message('clientv2-filter-gender')}
              allLabel={allLabel}
              values={filters.gender}
              options={[
                { value: 'Male', label: `♂ ${message('Male')}` },
                { value: 'Female', label: `♀ ${message('Female')}` },
                { value: UNSPECIFIED_GENDER, label: `⚲ ${message('clientv2-filter-unspecified')}` }
              ]}
              onAll={() => onChange({ ...filters, gender: [] })}
              onToggle={value => toggleMulti('gender', value)}
            />

            <FilterLabel>{message('Age')}</FilterLabel>
            <Stack
              direction="row"
              sx={{ justifyContent: 'space-between', color: 'text.secondary', typography: 'caption' }}
            >
              <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {filters.ageMin}
              </Box>
              <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {filters.ageMax}
              </Box>
            </Stack>
            {/* V1's age control is ONE track with two thumbs (searchFilter.js
                TransparentValueLabelMySlider value=[ageMin, ageMax]) — MUI's
                range Slider is exactly that. `disableSwap` keeps each thumb on
                its own side: dragged or typed past the other, it stops there,
                as the two hand-stacked inputs used to clamp. */}
            <Slider
              min={0}
              max={100}
              value={[filters.ageMin, filters.ageMax]}
              disableSwap
              getAriaLabel={index => message(index === 0 ? 'clientv2-filter-age-min' : 'clientv2-filter-age-max')}
              onChange={(_event, next) => {
                const [ageMin, ageMax] = next as number[];
                onChange({ ...filters, ageMin, ageMax });
              }}
            />

            <ChipGroup
              label={message('clientv2-filter-source')}
              allLabel={allLabel}
              values={filters.source}
              options={[
                { value: 'Warm', label: message('Warm') },
                { value: 'Refer', label: message('Refer') },
                { value: 'Cold', label: message('Cold') }
              ]}
              onAll={() => onChange({ ...filters, source: [] })}
              onToggle={value => toggleMulti('source', value)}
            />

            <FilterLabel htmlFor="clientv2-filter-referrer">{message('Referrer')}</FilterLabel>
            <ReferrerField
              value={filters.referrerName}
              options={options.referrers}
              label={message('Referrer')}
              placeholder={message('clientv2-filter-referrer-placeholder')}
              onChange={referrerName => onChange({ ...filters, referrerName })}
            />

            {/* The same ChipGroup as every other multi-select in this panel,
                which is what it should have been all along: it was the one
                group here built by hand, and it showed — flat #e0e0e0 blocks
                with no 全部 chip, sitting between nine rows of bordered white
                pills that all have one.
                Multi-select is unchanged (D + I still means「D or I」); what it
                gains is the 全部 chip that clears the letters, the same way
                every neighbour clears itself.
                The per-letter tints the selected buttons used to take
                (#EA8462 for D and so on) go with the hand-rolled markup. They
                were a fourth vocabulary of colour inside a panel whose chips
                say「chosen」in one blue, and DISC's own colours already live
                where they mean something — on the drawer's readout, where a
                letter is a fact about one client rather than a filter term. */}
            <ChipGroup
              label={message('clientv2-filter-disc')}
              allLabel={allLabel}
              values={filters.personality}
              options={personalityOptions.map(personality => ({
                value: personality,
                label: personality === UNSET_PERSONALITY ? message('clientv2-filter-disc-unset') : personality
              }))}
              onAll={() => onChange({ ...filters, personality: [] })}
              onToggle={value => toggleMulti('personality', value)}
            />

            {/*
             * Holdings and gaps are two groups rather than one tri-state chip
             * per category: the pairing 「有人壽」+「缺乏醫療」 is the
             * cross-sell query this exists for, and it needs both to be
             * selectable at once. OR inside each, AND between them.
             */}
            <ChipGroup
              label={message('clientv2-filter-policy-holding')}
              allLabel={allLabel}
              values={filters.policyHoldings}
              options={POLICY_CATEGORIES.map(category => ({
                value: category,
                label: message(POLICY_HOLDING_LABEL_KEYS[category])
              }))}
              onAll={() => onChange({ ...filters, policyHoldings: [] })}
              onToggle={value => togglePolicy('policyHoldings', value)}
            />

            <ChipGroup
              label={message('clientv2-filter-policy-gap')}
              allLabel={allLabel}
              values={filters.policyGaps}
              options={POLICY_CATEGORIES.map(category => ({
                value: category,
                label: message(POLICY_GAP_LABEL_KEYS[category])
              }))}
              onAll={() => onChange({ ...filters, policyGaps: [] })}
              onToggle={value => togglePolicy('policyGaps', value)}
            />

            <FilterLabel>{message('clientv2-filter-coverage')}</FilterLabel>
            <RangeControl
              label={`${message('Life_insured')} (${currency})`}
              value={filters.lifeCoverage}
              max={3000000}
              step={100000}
              comparator="<"
              onChange={lifeCoverage => onChange({ ...filters, lifeCoverage })}
            />
            <RangeControl
              label={`${message('CI_insured')} (${currency})`}
              value={filters.ciCoverage}
              max={2000000}
              step={100000}
              comparator="<"
              onChange={ciCoverage => onChange({ ...filters, ciCoverage })}
            />
            <RangeControl
              label={`${message('MonthlyIncome_short')} (${currency}/${message('month')})`}
              value={filters.monthlyIncome}
              max={60000}
              step={1000}
              comparator=">"
              onChange={monthlyIncome => onChange({ ...filters, monthlyIncome })}
            />
            <RangeControl
              label={`${message('MonthlySaving_short')} (${currency}/${message('month')})`}
              value={filters.monthlySaving}
              max={30000}
              step={500}
              comparator="<"
              onChange={monthlySaving => onChange({ ...filters, monthlySaving })}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 1.75, borderColor: 'grey.200' }} />

        <FilterLabel>
          {message('clientv2-family-tree')}
          <Button
            size="small"
            data-testid="manage-families"
            disabled={!onManageFamilies}
            startIcon={<Pencil size={14} aria-hidden="true" />}
            onClick={onManageFamilies}
            sx={{ minWidth: 0, px: 0.75, py: 0.25, typography: 'caption', fontWeight: 600 }}
          >
            {message('clientv2-filter-edit')}
          </Button>
        </FilterLabel>
        {/* Family chips stay amber, the colour of the 🏠 family badge on the
            client rows, so a family reads the same in the list and here. */}
        <ChipRow label={message('clientv2-family-tree')}>
          {options.families.map(family => (
            <FilterChip
              key={family}
              label={family}
              color="warning"
              size="small"
              active={filters.families.includes(family)}
              onClick={() => onChange({ ...filters, families: editFilterArray(filters.families, family) })}
            />
          ))}
        </ChipRow>

        <FilterLabel>{message('clientv2-filter-tag-cloud')}</FilterLabel>
        <ChipRow label={message('clientv2-filter-tag-cloud')}>
          {options.tags.map(tag => (
            <FilterChip
              key={tag.text}
              label={tag.text}
              size="small"
              tagColor={tag.color || null}
              active={filters.tags.includes(tag.text)}
              onClick={() => onChange({ ...filters, tags: editFilterArray(filters.tags, tag.text) })}
            />
          ))}
        </ChipRow>
      </Paper>
    </aside>
  );
};

export default injectIntl(SmartFilterSidebar);
